import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import BulkActionsBar, { bulkIcons } from "../components/BulkActionsBar";
import BulkValueModal from "../components/BulkValueModal";
import "./ItemMaster.css";

const API = "/material-master";

const emptyForm = () => ({
  name: "",
  description: "",
  unit: "",
});

function MaterialModal({ material, onClose, onSaved }) {
  const [form, setForm] = useState(
    material
      ? {
          name: material.name || "",
          description: material.description || "",
          unit: material.unit || "",
          gst: material.gst ?? "",
          rate: material.rate ?? "",
        }
      : emptyForm()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const h = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!String(form.name || "").trim() || !String(form.description || "").trim()) {
      setError("Name and description are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: String(form.name).trim(),
        description: String(form.description).trim(),
        unit: String(form.unit || "").trim(),
      };

      if (material) await api.put(`${API}/${material._id}`, payload);
      else await api.post(API, payload);

      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save material.");
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div className="im-overlay" onClick={onClose}>
      <div className="im-modal" onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <h3>{material ? "Edit Material" : "New Material"}</h3>
          <button className="im-icon-btn" onClick={onClose} type="button">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="im-modal-body">
          {error && <div className="im-alert">{error}</div>}
          <form id="mm-form" onSubmit={submit}>
            <div className="im-field">
              <label>
                Name <span className="im-req">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={h}
                required
                placeholder="Material name"
              />
            </div>

            <div className="im-field">
              <label>
                Description <span className="im-req">*</span>
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={h}
                rows={3}
                required
                minLength={1}
                placeholder="What you buy from the vendor (size, grade, use)…"
              />
            </div>

            <div className="im-row">
              <div className="im-field">
                <label>Unit</label>
                <input
                  name="unit"
                  value={form.unit}
                  onChange={h}
                  placeholder="e.g. sqft, rft, nos"
                />
              </div>

             
            </div>

            
          </form>
        </div>

        <div className="im-modal-footer">
          <button type="button" className="im-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="mm-form"
            className="im-btn-submit"
            disabled={saving}
          >
            {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default function MaterialMaster() {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showExcelMenu, setShowExcelMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkValue, setBulkValue] = useState("");

  const LIMIT = 20;
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const canDelete = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search) params.search = search;
      const res = await api.get(API, { params });
      setMaterials(res.data.data.materials || []);
      setTotal(res.data.data.pagination?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => materials.some((m) => m._id === id)));
  }, [materials]);

  useEffect(() => {
    const close = () => setShowExcelMenu(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const openAdd = () => {
    setEditMaterial(null);
    setShowModal(true);
  };
  const openEdit = (m) => {
    setEditMaterial(m);
    setShowModal(true);
  };

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedMaterials = useMemo(() => materials.filter((m) => selectedSet.has(m._id)), [materials, selectedSet]);
  const allVisibleSelected = materials.length > 0 && materials.every((m) => selectedSet.has(m._id));
  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id));
  };
  const selectAllVisible = () => setSelectedIds(materials.map((m) => m._id));
  const clearSelection = () => setSelectedIds([]);
  const openBulkEdit = () => {
    const first = selectedMaterials[0];
    if (first) openEdit(first);
  };
  const openSetUnit = () => {
    setBulkValue(selectedMaterials[0]?.unit || "");
    setBulkModal("unit");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    setDeleting(id);
    try {
      await api.delete(`${API}/${id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!canDelete || selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected material(s)?`)) return;
    try {
      await Promise.all(selectedIds.map((id) => api.delete(`${API}/${id}`)));
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Bulk delete failed.");
    }
  };

  const applyBulkUnit = async () => {
    const unit = String(bulkValue || "").trim();
    try {
      await Promise.all(selectedMaterials.map((m) => api.put(`${API}/${m._id}`, { unit })));
      setBulkModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Bulk update failed.");
    }
  };

  const handleExport = async () => {
    const res = await fetch("http://localhost:5000/api/material-master/export", {
      headers: { Authorization: `Bearer ${localStorage.getItem("ts_token")}` },
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "material-master.xlsx";
    a.click();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("http://localhost:5000/api/material-master/import", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("ts_token")}` },
        body: formData,
      });
      const data = await res.json();
      alert(data.message || "Import successful");
      load();
    } catch {
      alert("Import failed");
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="im-root slide-up">
      <div className="im-toolbar">
        <div className="im-toolbar-left" style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.45 }}>
            <strong>Purchase materials</strong> — plywood, hardware, adhesives, etc. from shops or vendors.
            Add lines to an order from <strong>Orders → + From Material Master</strong>.
          </div>
        </div>
        <div className="im-toolbar-right">
          {isAdmin && (
            <>
              <button className="im-btn-add" onClick={openAdd}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Material
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
                <button className="im-btn-export" onClick={() => setShowExcelMenu((s) => !s)}>
                  📊 Excel ▼
                </button>

                {showExcelMenu && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "45px",
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{ padding: "10px", cursor: "pointer" }}
                      onClick={() => {
                        document.getElementById("mm-excel-import").click();
                        setShowExcelMenu(false);
                      }}
                    >
                      Import Excel
                    </div>
                    <div
                      style={{ padding: "10px", cursor: "pointer" }}
                      onClick={() => {
                        handleExport();
                        setShowExcelMenu(false);
                      }}
                    >
                      Export Excel
                    </div>
                  </div>
                )}

                <input type="file" id="mm-excel-import" hidden accept=".xlsx,.xls" onChange={handleImport} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="im-search-bar">
        <div className="im-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="im-search-ic">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="im-search-input"
            placeholder="Search materials…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <span className="im-total-count">{total} materials</span>
      </div>

      <div className="im-table-wrap">
        <table className="im-table" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              {isAdmin && (
                <th style={{ width: 42 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                    aria-label="Select all visible materials"
                  />
                </th>
              )}
              <th>Name</th>
              <th>Description</th>
              <th>Unit</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 3} className="im-td-center">
                  <div className="im-spinner" />
                </td>
              </tr>
            ) : materials.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 3} className="im-td-center im-empty">
                  <p>No materials found</p>
                </td>
              </tr>
            ) : (
              materials.map((m) => (
                <tr key={m._id}>
                  {isAdmin && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(m._id)}
                        onChange={(e) => toggleSelected(m._id, e.target.checked)}
                        aria-label={`Select ${m.name}`}
                      />
                    </td>
                  )}
                  <td className="im-td-name">{m.name}</td>
                  <td className="im-td-desc">{m.description || "—"}</td>
                  <td>
                    <span className="im-pill">{m.unit || "—"}</span>
                  </td>
                 
                  {isAdmin && (
                    <td className="im-td-actions">
                      <button className="im-action-btn im-edit" onClick={() => openEdit(m)} type="button">
                        ✏️
                      </button>
                      <button
                        className="im-action-btn im-del"
                        onClick={() => handleDelete(m._id)}
                        disabled={deleting === m._id}
                        type="button"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="im-pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="im-page-btn">
            ← Prev
          </button>
          <span className="im-page-info">
            Page {page} of {totalPages} · {total} materials
          </span>
          <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="im-page-btn">
            Next →
          </button>
        </div>
      )}

      {showModal && (
        <MaterialModal
          material={editMaterial}
          onClose={() => {
            setShowModal(false);
            setEditMaterial(null);
          }}
          onSaved={load}
        />
      )}

      <BulkActionsBar
        count={selectedIds.length}
        actions={[
          { key: "edit", label: "Edit First", icon: bulkIcons.edit, onClick: openBulkEdit },
          { key: "unit", label: "Set Unit", icon: bulkIcons.set, onClick: openSetUnit },
          { key: "all", label: "Select All", icon: bulkIcons.selectAll, onClick: selectAllVisible },
          { key: "none", label: "Deselect All", icon: bulkIcons.clear, onClick: clearSelection },
          canDelete && { key: "delete", label: "Delete", icon: bulkIcons.delete, onClick: handleBulkDelete, danger: true },
        ]}
      />

      {bulkModal === "unit" && (
        <BulkValueModal
          title="Set unit"
          count={selectedIds.length}
          value={bulkValue}
          onChange={setBulkValue}
          onClose={() => setBulkModal(null)}
          onApply={applyBulkUnit}
          placeholder="e.g. sqft, rft, nos"
        />
      )}
    </div>
  );
}
